import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useCurrentUser } from '../../../../contexts/UserContext';
import { useViewerStore } from '../../../../store/store';
import {
    formatEditorEscalator,
    formatEditorMoney,
    formatEditorMultiplier,
    formatEditorPpw,
    formatEditorProduction,
    formatEditorRate,
    formatEditorSystemKw,
    useEditorPricingStore,
    useEditorPricingSync,
} from './editorPricing';

const stopCanvasGesture = (event: React.SyntheticEvent) => event.stopPropagation();

/** Bottom-left pricing card, opened from the canvas toolbar pricing button. */
export const EditorPricingRecommendation: React.FC = () => {
    const { hasCapability } = useCurrentUser();
    const canShowPricing = hasCapability('Show_OneButton');
    const loadingCompleted = useViewerStore((state) => state.loadingCompleted);
    const isViewportOnlyEmbed = useViewerStore((state) => state.isViewportOnlyEmbed);
    const isDashboardOpen = useViewerStore((state) => state.isDashboardOpen);
    const siteLoadingError = useViewerStore((state) => state.siteLoadingError);
    const status = useEditorPricingStore((state) => state.status);
    const options = useEditorPricingStore((state) => state.options);
    const recommendation = useEditorPricingStore((state) => state.recommendation);
    const blockReason = useEditorPricingStore((state) => state.blockReason);
    const errorMessage = useEditorPricingStore((state) => state.errorMessage);
    const usedMultiplier = useEditorPricingStore((state) => state.usedMultiplier);
    const usedSystemKw = useEditorPricingStore((state) => state.usedSystemKw);
    const usedProduction = useEditorPricingStore((state) => state.usedAnnualProductionKwh);
    const cardOpen = useEditorPricingStore((state) => state.cardOpen);
    const setCardOpen = useEditorPricingStore((state) => state.setCardOpen);
    const [modalOpen, setModalOpen] = useState(false);

    useEditorPricingSync(
        canShowPricing &&
            Boolean(loadingCompleted) &&
            !isViewportOnlyEmbed &&
            !isDashboardOpen &&
            !siteLoadingError?.trim(),
    );

    if (!canShowPricing || !cardOpen) return null;

    const metricText = (value: string): string => {
        if (blockReason) return '—';
        if (status === 'loading') return 'Loading…';
        return value;
    };

    return (
        <>
            <section
                className="editor-pricing-recommendation"
                aria-label="Live solar pricing recommendation"
                onPointerDown={stopCanvasGesture}
                onTouchStart={stopCanvasGesture}
                onMouseDown={stopCanvasGesture}
            >
                <header className="editor-pricing-recommendation__head">
                    <div>
                        <span>Live pricing</span>
                        <strong>{recommendation?.partner ?? 'Recommendation'}</strong>
                    </div>
                    {recommendation ? (
                        <small>
                            {formatEditorEscalator(recommendation.escalatorPercent)} escalator
                        </small>
                    ) : null}
                    <button
                        type="button"
                        className="editor-pricing-recommendation__close"
                        aria-label="Hide pricing"
                        onClick={() => setCardOpen(false)}
                    >
                        ×
                    </button>
                </header>
                <dl className="editor-pricing-recommendation__metrics">
                    <div>
                        <dt>EPC</dt>
                        <dd>{metricText(formatEditorMoney(recommendation?.epc ?? null))}</dd>
                    </div>
                    <div>
                        <dt>PPW</dt>
                        <dd>{metricText(formatEditorPpw(recommendation?.ppw ?? null))}</dd>
                    </div>
                    <div>
                        <dt>Min rate</dt>
                        <dd>{metricText(formatEditorRate(recommendation?.minRate ?? null))}</dd>
                    </div>
                </dl>
                <dl className="editor-pricing-recommendation__inputs">
                    <div>
                        <dt>System size</dt>
                        <dd>{formatEditorSystemKw(usedSystemKw)}</dd>
                    </div>
                    <div>
                        <dt>Production</dt>
                        <dd>{formatEditorProduction(usedProduction)}</dd>
                    </div>
                    <div>
                        <dt>Multiplier</dt>
                        <dd>{formatEditorMultiplier(usedMultiplier)}</dd>
                    </div>
                </dl>
                {blockReason ? (
                    <p className="editor-pricing-recommendation__note">{blockReason}</p>
                ) : null}
                {!blockReason && status === 'ready' && recommendation?.ppwReason ? (
                    <p className="editor-pricing-recommendation__note">
                        {recommendation.ppwReason}
                    </p>
                ) : null}
                {!blockReason && status === 'error' && errorMessage ? (
                    <p className="editor-pricing-recommendation__error">{errorMessage}</p>
                ) : null}
                <button
                    type="button"
                    className="editor-pricing-recommendation__all"
                    disabled={options.length === 0}
                    onClick={() => setModalOpen(true)}
                >
                    View all options
                </button>
            </section>

            {modalOpen
                ? createPortal(
                      <>
                          <button
                              type="button"
                              className="editor-pricing-options__backdrop"
                              aria-label="Close pricing options"
                              onClick={() => setModalOpen(false)}
                          />
                          <section
                              className="editor-pricing-options"
                              role="dialog"
                              aria-modal="true"
                              aria-labelledby="editor-pricing-options-title"
                          >
                              <header className="editor-pricing-options__head">
                                  <div>
                                      <h2 id="editor-pricing-options-title">Solar pricing options</h2>
                                      <p>
                                          Sandbox Pricing Calculator rates at{' '}
                                          {formatEditorSystemKw(usedSystemKw)},{' '}
                                          {formatEditorProduction(usedProduction)} and multiplier{' '}
                                          {formatEditorMultiplier(usedMultiplier)}
                                      </p>
                                  </div>
                                  <button
                                      type="button"
                                      aria-label="Close"
                                      onClick={() => setModalOpen(false)}
                                  >
                                      ×
                                  </button>
                              </header>
                              <div className="editor-pricing-options__body">
                                  <table>
                                      <thead>
                                          <tr>
                                              <th>Partner</th>
                                              <th>Method</th>
                                              <th>Term</th>
                                              <th>Escalator</th>
                                              <th>Min rate</th>
                                              <th>Rate cap</th>
                                              <th>PPW</th>
                                              <th>EPC</th>
                                          </tr>
                                      </thead>
                                      <tbody>
                                          {options.map((option) => (
                                              <tr
                                                  key={option.id}
                                                  className={
                                                      option.id === recommendation?.id
                                                          ? 'editor-pricing-options__recommended'
                                                          : undefined
                                                  }
                                              >
                                                  <td>
                                                      <strong>{option.partner}</strong>
                                                      {option.id === recommendation?.id ? (
                                                          <span>Recommended</span>
                                                      ) : null}
                                                  </td>
                                                  <td>{option.financeMethod}</td>
                                                  <td>
                                                      {option.term != null
                                                          ? `${option.term} yr`
                                                          : '—'}
                                                  </td>
                                                  <td>
                                                      {formatEditorEscalator(option.escalatorPercent)}
                                                  </td>
                                                  <td>{formatEditorRate(option.minRate)}</td>
                                                  <td>{formatEditorRate(option.rateCap)}</td>
                                                  <td title={option.ppwReason ?? undefined}>
                                                      {formatEditorPpw(option.ppw)}
                                                  </td>
                                                  <td title={option.ppwReason ?? undefined}>
                                                      {formatEditorMoney(option.epc)}
                                                  </td>
                                              </tr>
                                          ))}
                                      </tbody>
                                  </table>
                              </div>
                          </section>
                      </>,
                      document.body,
                  )
                : null}
        </>
    );
};

export default EditorPricingRecommendation;
